import React, { useState, useEffect } from 'react';
import { BrowserRouter, unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { AuthProvider } from '../features/auth/stores/AuthContext';
import { ThemeProvider } from '../context/ThemeContext';
import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import ExcelOAuthLandingRedirect from './ExcelOAuthLandingRedirect';
import CookieConsent from '../features/consent/CookieConsent';
import PathbuddyMovedBanner from '../features/marketing/PathbuddyMovedBanner';
import { getAppBasename, servesEduAtRoot } from '../config/appBase';
import { createRootAliasHistory } from './rootAliasHistory';

/**
 * Where the Edu home is the site root (ivoriai.com/), the router runs under
 * /edu on a history that shows router "/edu" as browser "/". Everywhere else
 * it is a plain BrowserRouter.
 */
export function ProductRouter({ basename, children }) {
  const [history] = useState(() => (
    basename && typeof window !== 'undefined' && servesEduAtRoot(window.location.hostname)
      ? createRootAliasHistory({ window })
      : null
  ));
  const future = { v7_startTransition: true, v7_relativeSplatPath: true };
  if (history) {
    return <HistoryRouter history={history} basename={basename} future={future}>{children}</HistoryRouter>;
  }
  return <BrowserRouter basename={basename || undefined} future={future}>{children}</BrowserRouter>;
}

const AppProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // "/edu" when the page loaded on the Edu home (ivoriai.com/) or under
  // ivoriai.com/edu (or localhost:3000/edu), otherwise "" (pathbuddy.com, the
  // old edu hosts, the company home at /about). Fixed at mount: the company
  // home and the edu app are separate page loads.
  const [basename] = useState(getAppBasename);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Enhanced performance monitoring
  useEffect(() => {
    // Monitor page load performance
    if ('performance' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          // Performance monitoring handled by web vitals
        }, 0);
      });
    }

    // Monitor memory usage (if supported)
    if ('memory' in performance) {
      const checkMemory = () => {
        const memInfo = performance.memory;
        const usagePercent = memInfo.usedJSHeapSize / memInfo.jsHeapSizeLimit;
        
        if (usagePercent > 0.9) {
          console.warn('High memory usage detected:', {
            used: Math.round(memInfo.usedJSHeapSize / 1048576) + 'MB',
            total: Math.round(memInfo.jsHeapSizeLimit / 1048576) + 'MB',
            percentage: Math.round(usagePercent * 100) + '%'
          });
        }
      };
      
      const memoryInterval = setInterval(checkMemory, 30000); // Check every 30 seconds
      return () => clearInterval(memoryInterval);
    }
  }, []);

  // Service Worker registration for PWA capabilities
  useEffect(() => {
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((registration) => {
            // Handle service worker updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New service worker available - could show update notification to user
                }
              });
            });
          })
          .catch(() => {
            // Silently fail service worker registration
          });
      });
    }
  }, []);

  // Connection quality detection
  useEffect(() => {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      const slowConnections = ['slow-2g', '2g', '3g'];
      
      const updateConnectionClass = () => {
        if (slowConnections.includes(connection.effectiveType)) {
          document.body.classList.add('low-bandwidth');
        } else {
          document.body.classList.remove('low-bandwidth');
        }
      };
      
      // Initial check
      updateConnectionClass();
      
      // Monitor connection changes
      connection.addEventListener('change', updateConnectionClass);
      
      return () => {
        connection.removeEventListener('change', updateConnectionClass);
      };
    }
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ProductRouter basename={basename}>
          {/* First in DOM order so the banner is the first Tab stop. */}
          <CookieConsent />
          {/* Visitors redirected from the retired pathbuddy.com / pathbuddy.app. */}
          <PathbuddyMovedBanner />
          <ExcelOAuthLandingRedirect />
          {/* Offline indicator */}
          {!isOnline && (
            <div className="offline-indicator">
              <div className="offline-content">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M1 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <span>You're offline. Some features may not work.</span>
              </div>
            </div>
          )}
          
          {children}
          </ProductRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default AppProvider;